import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { Button, Message } from '@getro/rombo';
import { Dropdown } from 'components/molecules/dropdown';
import { Card } from 'components/atoms/Card';
import { GoogleIcon } from 'components/atoms/googleSignInButton/components/googleIcon';
import { Zap, Contact, MoreVertical, Trash } from 'lucide-react';
import { IntegrationStatus } from 'components/atoms/IntegrationStatus';
import DisconnectGoogleAccount from './DisconnectGoogleAccount';

const NOT_CONNECTED_TITLE = 'Google mail and calendar';
const CARD_DESCRIPTION =
  'Sync your Google contacts, emails, and calendar events to keep your network up to date. ' +
  'Choose what information to share and with whom.';

export const GoogleIntegrationCard = ({
  googleSync: {
    email,
    syncStatus: status = 'not_connected',
    scopes = [],
    contactsSynced = 0,
    contactsSyncEnabled,
    relationshipStrengthEnabled,
    permissionsMissing,
    lastSyncedAt = null,
  } = {},
  onConnect,
  onReconnect,
  onRemoveConfirm,
  isRemoving,
  variant = 'default',
}) => {
  const [showRemove, setShowRemove] = useState(false);
  const isOnboarding = variant === 'onboarding';
  const isNotConnected = status === 'not_connected';
  // No actions while an async teardown is in flight; the card returns to the
  // connect state once the account is fully purged.
  const isDisconnecting = status === 'disconnecting';
  // Driven by the standalone `permissionsMissing` flag so the reconnect alert
  // still shows when a concurrent backfill makes the pill read 'syncing'; the
  // status check keeps it working if the flag hasn't shipped from the API yet.
  const showPermissionsAlert = (permissionsMissing || status === 'permissions_missing') && !isDisconnecting;

  const grantedScopes = scopes || [];
  // Both the legacy People-API scopes (pre-GET-268 accounts) and the
  // interaction-stream scopes (new modal) count as sync activity. The
  // "Last synced on …" row is per-account, not per-source, so it stays
  // gated on any of these grants — turning a single toggle off must not
  // hide the row entirely.
  const SYNC_ACTIVITY_SCOPES = [
    'https://www.googleapis.com/auth/contacts.readonly',
    'https://www.googleapis.com/auth/contacts.other.readonly',
    'https://www.googleapis.com/auth/gmail.metadata',
    'https://www.googleapis.com/auth/calendar.events.readonly',
    'https://www.googleapis.com/auth/calendar.readonly',
  ];
  const hasSyncActivity = SYNC_ACTIVITY_SCOPES.some((scope) => grantedScopes.includes(scope));
  // Two distinct populations land on this card:
  //   1. New-flow accounts have walk scopes granted (gmail.metadata /
  //      calendar.events.readonly, or pre-GET-615 calendar.readonly). They've
  //      been through the modal at least once, so `contacts_sync_enabled`
  //      reflects an explicit user choice — trust it strictly. A row with
  //      walks granted but contacts sync OFF must hide the count.
  //   2. Legacy contacts-only accounts predate the column and only carry
  //      People-API scopes. The migration leaves them at the `false` default
  //      even though their grant is still producing contacts, so treat them
  //      as implicit-true and gate on scope.
  // Distinguishing on "any walk scope granted" is what keeps both cases right
  // without needing the backend to send null for unmigrated rows.
  const hasWalkScope =
    grantedScopes.includes('https://www.googleapis.com/auth/gmail.metadata') ||
    grantedScopes.includes('https://www.googleapis.com/auth/calendar.events.readonly') ||
    grantedScopes.includes('https://www.googleapis.com/auth/calendar.readonly');
  const hasLegacyContactsScope =
    grantedScopes.includes('https://www.googleapis.com/auth/contacts.readonly') ||
    grantedScopes.includes('https://www.googleapis.com/auth/contacts.other.readonly');
  const contactsPipelineActive = hasWalkScope ? contactsSyncEnabled === true : hasLegacyContactsScope;
  const shouldShowContactsSynced = status !== 'syncing' && contactsPipelineActive;

  // Per-source state shown as a check/dash list (mirrors the Team Members
  // popover). Contacts reuses the two-population gate above so legacy accounts
  // still read as enabled.
  const featureRows = [
    {
      key: 'relationship_strength',
      label: 'Relationship strength enrichment',
      enabled: relationshipStrengthEnabled === true,
    },
    { key: 'contacts', label: 'Contacts sync', enabled: contactsPipelineActive },
  ];

  const description = (
    <Text color="text.main" fontSize="14px" lineHeight="1.4">
      {CARD_DESCRIPTION}
    </Text>
  );

  return (
    <Flex
      sx={{
        gap: '16px',
        p: '16px',
        border: '1px solid',
        borderColor: '#dee4ed',
        borderRadius: '4px',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '600px',
      }}
    >
      <Flex sx={{ gap: '16px' }} alignItems="flex-start" width="100%">
        {/* Icon column */}
        <GoogleIcon width="32px" height="32px" />

        {/* Content column */}
        <Flex flexDirection="column" sx={{ gap: '8px' }} flex={1}>
          {isNotConnected ? (
            <Flex sx={{ gap: '16px', alignItems: 'flex-start', minWidth: 0 }}>
              <Flex flexDirection="column" sx={{ gap: '4px', flex: 1, minWidth: 0 }}>
                <Text color="text.dark" fontWeight="500" fontSize="14px" lineHeight="1.4">
                  {NOT_CONNECTED_TITLE}
                </Text>
                {description}
              </Flex>
              <Button size="small" onClick={onConnect} variant="primary">
                Connect
              </Button>
            </Flex>
          ) : (
            <>
              <Flex alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
                <Text
                  color="text.dark"
                  fontWeight="500"
                  fontSize="14px"
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {email || NOT_CONNECTED_TITLE}
                </Text>
                <Flex sx={{ alignItems: 'center', gap: '8px' }}>
                  <IntegrationStatus status={status} />
                  {!isOnboarding && !isDisconnecting && (
                    <Box
                      sx={{
                        minWidth: '24px',
                        maxHeight: '24px',
                        height: '24px',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        alignItems: 'center',
                      }}
                      tx="dataTable"
                      data-testid="row-action"
                      display="flex"
                    >
                      <Dropdown
                        placement="right-start"
                        trigger={
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            width="24px"
                            height="24px"
                            sx={{ cursor: 'pointer', svg: { color: 'text.main' } }}
                            aria-label="More actions"
                          >
                            <Box as={MoreVertical} size="16px" aria-hidden="true" />
                          </Flex>
                        }
                      >
                        <Card
                          sx={{ cursor: 'pointer', width: '228px', gap: '4px' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Flex
                            sx={{
                              gap: '8px',
                              py: '4px',
                              color: 'text.error',
                              '&:hover': { color: 'text.error' },
                            }}
                            alignItems="center"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowRemove(true);
                            }}
                          >
                            <Box as={Trash} size="16px" aria-hidden="true" />
                            <Text fontSize="14px" fontWeight="500" lineHeight="24px">
                              Disconnect
                            </Text>
                          </Flex>
                        </Card>
                      </Dropdown>
                    </Box>
                  )}
                </Flex>
              </Flex>

              {description}
              <Flex flexDirection="column" sx={{ gap: '8px', mt: '16px' }}>
                <Flex
                  sx={{ gap: '16px', rowGap: '8px', flexWrap: 'wrap', alignItems: 'center' }}
                  data-testid="google-feature-rows"
                >
                  {featureRows.map(({ key, label, enabled }) => (
                    <Flex key={key} sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                      <Box
                        as="span"
                        aria-label={enabled ? 'enabled' : 'not enabled'}
                        sx={{
                          width: '16px',
                          textAlign: 'center',
                          color: enabled ? 'green.600' : 'text.subtle',
                        }}
                      >
                        {enabled ? '✓' : '—'}
                      </Box>
                      <Text color="text.main" fontSize="14px">
                        {label}
                      </Text>
                    </Flex>
                  ))}
                </Flex>
                {hasSyncActivity && (
                  <>
                    {shouldShowContactsSynced && (
                      <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                        <Box as={Contact} width="16px" height="16px" aria-hidden="true" />
                        <Text color="text.main" fontSize="14px">
                          {contactsSynced} contacts synced
                        </Text>
                      </Flex>
                    )}
                    <Flex sx={{ gap: '8px', alignItems: 'center', color: 'text.main' }}>
                      <Box as={Zap} width="16px" height="16px" aria-hidden="true" />
                      {status === 'syncing' ? (
                        <Text color="text.main" fontSize="14px">
                          Sync in progress, this may take up to 3 hours.
                        </Text>
                      ) : (
                        <Text color="text.main" fontSize="14px">
                          {`Last synced on ${
                            lastSyncedAt
                              ? new Date(lastSyncedAt).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                })
                              : '-'
                          }`}
                        </Text>
                      )}
                    </Flex>
                  </>
                )}
                {showPermissionsAlert && (
                  <Message
                    type="warning"
                    content={
                      <Text>
                        Some permissions are missing, affected features won&apos;t work until you reconnect.{' '}
                        <Box
                          as="button"
                          type="button"
                          onClick={onReconnect}
                          sx={{
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                            color: 'inherit',
                          }}
                        >
                          Reconnect now
                        </Box>
                        .
                      </Text>
                    }
                  />
                )}
              </Flex>
            </>
          )}
        </Flex>
      </Flex>
      {showRemove && (
        <DisconnectGoogleAccount
          isSubmitting={isRemoving}
          onCancel={() => setShowRemove(false)}
          onConfirm={async () => {
            await onRemoveConfirm?.();
            setShowRemove(false);
          }}
        />
      )}
    </Flex>
  );
};

GoogleIntegrationCard.propTypes = {
  googleSync: PropTypes.object,
  onConnect: PropTypes.func.isRequired,
  onReconnect: PropTypes.func,
  onRemoveConfirm: PropTypes.func,
  isRemoving: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'onboarding']),
};

GoogleIntegrationCard.defaultProps = {
  // Destructuring at the call site uses `= {}` as a fallback, so the
  // default here must not be `null` (destructuring `null` throws). `{}` lets
  // the card render the not-connected state when no account is loaded yet.
  googleSync: {},
  onReconnect: undefined,
  onRemoveConfirm: undefined,
  isRemoving: false,
  variant: 'default',
};
